export const MAX_EXTERNAL_REDEEM_ITEMS = 100;
export const MAX_EXTERNAL_CDK_LENGTH = 512;
export const MAX_EXTERNAL_ACCESS_TOKEN_LENGTH = 8192;

const CONTROL_CHAR_PATTERN = /[\u0000-\u001f\u007f]/;

type RecordLike = Record<string, unknown>;

export type ExternalRedeemSubmitItem = {
  cdkey: string;
  access_token: string;
};

export type ExternalRedeemSubmitBody = {
  items: ExternalRedeemSubmitItem[];
};

export type ExternalRedeemStatusBody = {
  cdkeys: string[];
};

function isRecord(value: unknown): value is RecordLike {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function cleanString(value: unknown) {
  return String(value ?? "").trim();
}

function hasControlChars(value: string) {
  return CONTROL_CHAR_PATTERN.test(value);
}

export function validateExternalRedeemSubmit(body: unknown) {
  const items = (body as { items?: unknown[] } | null)?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return { error: "items 不能为空" };
  }
  if (items.length > MAX_EXTERNAL_REDEEM_ITEMS) {
    return { error: "一次最多提交 100 个 CDK" };
  }

  const seen = new Set<string>();
  const normalized: ExternalRedeemSubmitItem[] = [];
  for (const item of items) {
    const row = isRecord(item) ? item : {};
    const cdkey = cleanString(row.cdkey);
    const accessToken = cleanString(row.access_token);
    if (!cdkey) return { error: "CDK 不能为空" };
    if (cdkey.length > MAX_EXTERNAL_CDK_LENGTH) return { error: "CDK 不能超过 512 个字符" };
    const key = cdkey.toLowerCase();
    if (seen.has(key)) return { error: "CDK 不可重复提交" };
    seen.add(key);
    if (!accessToken) return { error: "access_token 不能为空" };
    if (accessToken.length > MAX_EXTERNAL_ACCESS_TOKEN_LENGTH) {
      return { error: "access_token 不能超过 8192 个字符" };
    }
    if (hasControlChars(accessToken)) return { error: "access_token 包含非法控制字符" };
    normalized.push({ cdkey, access_token: accessToken });
  }

  return { body: { items: normalized } satisfies ExternalRedeemSubmitBody };
}

export function validateExternalRedeemStatus(body: unknown) {
  const cdkeys = (body as { cdkeys?: unknown[] } | null)?.cdkeys;
  if (!Array.isArray(cdkeys) || cdkeys.length === 0) {
    return { error: "cdkeys 不能为空" };
  }
  if (cdkeys.length > MAX_EXTERNAL_REDEEM_ITEMS) {
    return { error: "一次最多查询 100 个 CDK" };
  }

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of cdkeys) {
    const cdkey = cleanString(raw);
    if (!cdkey) return { error: "CDK 不能为空" };
    if (cdkey.length > MAX_EXTERNAL_CDK_LENGTH) return { error: "CDK 不能超过 512 个字符" };
    const key = cdkey.toLowerCase();
    if (seen.has(key)) return { error: "CDK 不能重复" };
    seen.add(key);
    normalized.push(cdkey);
  }

  return { body: { cdkeys: normalized } satisfies ExternalRedeemStatusBody };
}

function rowString(row: RecordLike, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

export function externalRedeemRowLabel(row: unknown) {
  if (!isRecord(row)) return "-";
  const displayStatus = rowString(row, ["display_status"]);
  const status = rowString(row, ["status"]);
  if (row.accepted === false || status === "rejected") return "提交失败";
  if (row.found === false || status === "not_found") return "未找到";
  if (status === "success") return "成功";
  if (status === "pending_dispatch") return "等待兑换";
  if (status === "running" || status === "dispatched") return "兑换中";
  if (status === "failed" || status === "timeout") return "失败";
  return displayStatus || status || "-";
}

export function externalRedeemRowMessage(row: unknown) {
  if (!isRecord(row)) return "-";
  return rowString(row, ["error_message", "reason", "message"]) || externalRedeemRowLabel(row);
}

export function externalRedeemRowStatus(row: unknown) {
  if (!isRecord(row)) return "-";
  if (row.accepted === false) return "rejected";
  if (row.found === false) return "not_found";
  return rowString(row, ["status"]) || "-";
}

export function shouldApplyExternalRedeemHistoryUpdate(currentStatus: string, nextStatus: string) {
  if (nextStatus === "not_found") return false;
  if (!currentStatus || currentStatus === "submitted") return true;
  if (currentStatus === "pending_dispatch" && nextStatus === "pending_dispatch") return true;
  return true;
}

export function externalRedeemProgressValue(status: string) {
  if (status === "success") return 100;
  if (status === "running" || status === "dispatched") return 72;
  if (status === "pending_dispatch") return 32;
  if (status === "failed" || status === "timeout" || status === "rejected") return 100;
  if (status === "not_found") return 0;
  return 12;
}

export function externalRedeemProgressText(status: string) {
  if (status === "success") return "兑换完成";
  if (status === "running" || status === "dispatched") return "系统正在处理兑换";
  if (status === "pending_dispatch") return "任务已入队，等待系统派发";
  if (status === "failed" || status === "timeout" || status === "rejected") return "兑换处理失败";
  if (status === "not_found") return "未找到对应任务";
  return "任务已创建";
}

export function externalRedeemTerminal(status: string) {
  return ["success", "failed", "timeout", "rejected", "not_found"].includes(status);
}
